import { HttpClient } from './httpClient.ts'
import { PickPublic } from '../lib/utils.ts'
import { PracticeHintType } from './vocabApi.ts'
import {
  AgeKeys,
  InterestKeys,
  SexKeys,
  GoalKeys,
  ProfessionKeys,
  ImproveKeys,
  DurationKeys,
  LangLevels,
} from '../data/onboardingSteps.ts'
import { AppLanguages, NativeAppLanguages } from '../translations/messages.ts'
import type { FrontendLocale } from '../lib/appLocalStorage.ts'

export type MessageStateHint =
  | 'hardcoded-question-two'
  | 'hardcoded-question-three'
  | 'hardcoded-question-four'

export type HintsType =
  | 'orthography_hint'
  | 'chat_onboarding_snowflake_hint'
  | 'show_positive_ortho'
  | 'start_vocabulary_hint'
  | null

export interface TrueOrFalseQuestion {
  title: string
  isCorrect: boolean
}

export interface ExerciseInfo {
  type: 'fill_the_gaps2' | 'match_words'
  exercise_count: number
}

export type SubscriptionPlan = '1M' | '3M' | '12M'
export type SubscriptionProvider = 'yakassa' | 'cp'

export type LessonType = 'lesson' | 'vocabulary' | null

export type ViewMode = 'readonly' | 'full'

export interface LessonStages {
  RU: Record<string, string>
  EN: Record<string, string>
  TR: Record<string, string>
  VI: Record<string, string>
}

export type SubscriptionStatus =
  | 'trial'
  | 'active'
  | 'inactive'
  | 'expired'
  | 'pending'
  | 'failed'
  | 'deleted'
export interface Subscription {
  id: number
  amount: number
  plan: SubscriptionPlan
  status: SubscriptionStatus
  subscription_date: string
  days: number
  days_estimated: number
  recurrent: boolean
  end: string | null
  start: string | null
  created: string
}
export interface MatchWord {
  word: string
  synonym: string
}

type FillTheGapsText = readonly (string | { word: string })[]
export interface FillTheGaps2Data {
  title: { RU: string; EN: string }
  text: FillTheGapsText
  options: readonly string[]
  incorrect: { RU: string; EN: string }
  vocab_item: string
}

export type Listening = readonly {
  text: string
  sound_url: string
  options: readonly string[]
}[]
export type FillTheGaps = readonly (string | { word: string })[]
export type WriteTheGaps = readonly (string | { word: string; hint: string })[]
export const demoLessonTag = 'demo_lesson'

export const HelpLessonTag = (tag: string): boolean => {
  return tag.includes('Onboarding')
}

export interface Chat {
  id: number
  course_id: number | null
  chat_title: string
  program_tag: string
  user: number
  next_program_tag: string | null
}

export interface Vocabulary {
  id: number
  word_original: string
  word_normal: string
  explain?: string
  context?: string
  translation?: string
  timestamp: string
  program_tag: string
  is_user_added: boolean
}

export interface ApiOnboardingInfo {
  goal: GoalKeys | ''
  profession: ProfessionKeys | ''
  interests: readonly InterestKeys[]
  sex: SexKeys | ''
  age: AgeKeys | ''
  improves: readonly ImproveKeys[]
  duration: DurationKeys | ''
  level: string
}

export interface Lesson {
  id: number
  title: string
  user: number
  message_id: number
}

type ProgramItemStatus = 'new' | 'active' | 'completed' | 'closed'
export interface ProgramItem {
  id: number
  level: string
  lesson: string
  tag: string
  program: string
  topic: string
  display_topic: string
  lesson_type: string
  lesson_name: string
  lesson_subject: string
  status: ProgramItemStatus
  order: number
  locked: boolean | null
  ai_rate?: {
    rate?: number
  }
}

export interface AiCommentSpellElement {
  text: string
  rule: string
  correct: string
}

export interface AiComment {
  spell: readonly AiCommentSpellElement[]
  hint?: HintsType
}

export interface RateAiComment {
  rate: 1 | 2 | 3
  points?: { stage_points: number; exercise_points: number }
}

export type InfoBlockType =
  | 'start_vocabulary_hint'
  | 'first_message'
  | 'orthography_hint'
  | 'disable_hints'
  | 'show_positive_ortho'

export interface IExercises {
  currentIndex: number
  exercise_count: number
  exercises: readonly ExerciseInfo[] | null
}

export interface ChatMessage {
  id: number
  message_text: string
  is_ai: boolean
  picture_url?: string
  listening?: Listening
  fill_the_gaps?: readonly FillTheGaps[]
  fill_the_gaps2?: readonly FillTheGaps2Data[]
  article?: {
    body: string
    header: string
  }
  true_or_false?: readonly TrueOrFalseQuestion[]
  write_the_gaps?: readonly WriteTheGaps[]
  match_words?: readonly MatchWord[]
  ai_comment?: AiComment
  ai_rate?: RateAiComment | null
  picture_urls?: readonly { url: string; title: string }[]
  parent_msg_id?: number
  info_block?: InfoBlockType | 'none'
  is_last: boolean
  is_hidden?: boolean
  translation?: string
  reply_hint?: {
    text: string
    translation: string
  }
  state?: string
  exercises?: IExercises
  contexts?: readonly string[]
}

export interface BalanceResponse {
  value: number
}

export interface SessionMetadata {
  programTag: string
  lessonType: LessonType
  hints?: PracticeHintType[]
}

export type RegisterSrc =
  | 'telegram'
  | 'email'
  | 'vk'
  | 'yandex'
  | 'google'
  | 'apple'

export interface UserCourse {
  id: number
  name: string
  slug: string
  frontend_locale?: FrontendLocale
  description?: string
  is_active?: boolean
}

export interface Student {
  name: string
  is_teacher?: boolean
  level: LangLevels
  onboarding_info: ApiOnboardingInfo
  date_joined?: string | null
  last_login?: string | null
  session_metadata?: SessionMetadata | null
  native_language: NativeAppLanguages | null
  register_src: RegisterSrc
  courses?: UserCourse[]
  is_psychology_speaking_available?: boolean
  psychology_lessons_completed?: number
  psychology_lessons_total?: number
}

export interface Stat {
  total: number
  new: number
  active?: number
  completed?: number
  vocab: { program: string; learned_count: number; total_count: number }[]
}

export interface UpdateStudent {
  name?: string
  level?: string
  onboarding_info?: ApiOnboardingInfo
}

export interface PatchStudent {
  name?: string
  level?: string
  onboarding_info?: ApiOnboardingInfo
  ui_lang?: AppLanguages
  ui_device?: 'mobile' | 'desktop'
  session_metadata?: SessionMetadata | null
}

export interface UpdatePassword {
  password: string
}

export interface UpdateEmail {
  email: string
}

export interface CloudPaymentsPaymentData {
  publicId: string
  description: string
  amount: number
  currency: string
  invoiceId: string
  accountId: string
  email: string
  skin: string
  data: {
    cloudPayments: {
      recurrent?: {
        interval: string
        period: number
      }
    }
  }
}

export interface YaKassaPaymentData {
  confirmation_token: string
}

export type Hints =
  | 'marketingPracticeHint'
  | 'marketingLearningProfileHint'
  | 'lessonsOnboarding'
  | 'practiceOnboarding'
  | 'chatOnboarding'
  | 'learningProfileOnboarding'

export type AccountHints = Record<Hints, boolean>

function updateProgram(item: ProgramItem) {
  item.program = item.program
  item.topic = item.topic
  return item
}

export type ChatApi = PickPublic<AppChatApi>
export class AppChatApi {
  async userPrograms(courseId?: number | null): Promise<ProgramItem[]> {
    const params = new URLSearchParams()
    if (courseId != null) params.set('course_id', String(courseId))
    const url =
      '/api/user_programs/' + (params.toString() ? `?${params.toString()}` : '')
    return this.httpClient
      .get<ProgramItem[]>(url)
      .then((x) => x.map(updateProgram))
  }

  async getUserCourses(): Promise<UserCourse[]> {
    return this.httpClient.get<UserCourse[]>('/api/user_courses/')
  }

  async userProgramsRealtime(courseId?: number | null): Promise<ProgramItem[]> {
    const params = new URLSearchParams()
    if (courseId != null) params.set('course_id', String(courseId))
    const url =
      '/api/user_programs/realtime/' +
      (params.toString() ? `?${params.toString()}` : '')

    return this.httpClient.get<ProgramItem[]>(url)
  }

  async userProgramComplete(tag: string, courseId?: number | null) {
    const params = new URLSearchParams()
    if (courseId != null) params.set('course_id', String(courseId))
    const url =
      `/api/user_program/complete/${tag}/` +
      (params.toString() ? `?${params.toString()}` : '')
    return this.httpClient.post<never>(url, {})
  }

  constructor(private httpClient: HttpClient) {}

  async version() {
    return this.httpClient.get<string>('/api/version/')
  }

  async unlockLesson(tag: string, courseId?: number | null) {
    const params = new URLSearchParams()
    if (courseId != null) params.set('course_id', String(courseId))
    const url =
      `/api/user_program/lock/${tag}/` +
      (params.toString() ? `?${params.toString()}` : '')
    return this.httpClient.patch<never>(url, {
      locked: false,
    })
  }

  async chat_list() {
    return this.httpClient.get<Chat[]>('/api/chat_list/')
  }

  chat_create(request: {
    user: number
    chat_title: string
    program_tag: string
    language?: 'RU' | 'EN'
    recreate?: boolean
    parameters: { user_vocab?: string[]; course_id: number }
  }) {
    return this.httpClient.post<Chat>('/api/chat_create/', request)
  }

  message_create(request: { chat: number; message_text: string }) {
    return this.httpClient.post<ChatMessage>('/api/message_create/', request)
  }

  message_process(messageId: number) {
    return this.httpClient.get<ChatMessage[]>(
      `/api/message/${messageId}/process/`,
    )
  }

  message_translate(messageId: number) {
    return this.httpClient.get<ChatMessage>(
      `/api/message/${messageId}/translate/`,
    )
  }

  message_reply_hint(messageId: number) {
    return this.httpClient.get<ChatMessage>(
      `/api/message/${messageId}/reply_hint/`,
    )
  }

  message_orthography_check(messageId: number) {
    return this.httpClient.get<ChatMessage>(
      `/api/message/${messageId}/orthography/`,
    )
  }

  async message_list(chatId: number): Promise<ChatMessage[]> {
    const response = await this.httpClient.get<ChatMessage[]>(
      `/api/message_list/${chatId}/`,
    )
    return response.map((x) => ({ ...x, is_ai: Boolean(x.is_ai) }))
  }

  async lesson_list(chatId: number): Promise<Lesson[]> {
    return await this.httpClient.get<Lesson[]>(`/api/lesson_list/${chatId}/`)
  }

  async message(messageId: number): Promise<ChatMessage> {
    return await this.httpClient.get<ChatMessage>(`/api/message/${messageId}/`)
  }

  async balance() {
    return await this.httpClient.get<BalanceResponse>(`/api/balance/`)
  }

  async getAccount() {
    return await this.httpClient.get<Student>(`/api/account/`)
  }

  async userProgramStages(tag: string, courseId?: number | null) {
    const params = new URLSearchParams()
    if (courseId != null) params.set('course_id', String(courseId))
    const url =
      `/api/user_program/stages/${tag}/` +
      (params.toString() ? `?${params.toString()}` : '')
    return await this.httpClient.get<LessonStages>(url)
  }

  async userProgramStage(tag: string, courseId?: number | null) {
    const params = new URLSearchParams()
    if (courseId != null) params.set('course_id', String(courseId))
    const url =
      `/api/user_program/stage/${tag}/` +
      (params.toString() ? `?${params.toString()}` : '')
    return await this.httpClient.get<{ stage: string }>(url)
  }

  async userProgramLastLesson(courseId?: number | null) {
    const params = new URLSearchParams()
    if (courseId != null) params.set('course_id', String(courseId))
    const url =
      '/api/user_programs/active/last/' +
      (params.toString() ? `?${params.toString()}` : '')
    return await this.httpClient.get<ProgramItem>(url)
  }

  async updateAccount(update: UpdateStudent, courseId?: number | null) {
    const params = new URLSearchParams()
    if (courseId != null) params.set('course_id', String(courseId))
    const url =
      '/api/account/' + (params.toString() ? `?${params.toString()}` : '')
    return await this.httpClient.put<Student>(url, update)
  }

  async patchAccount(update: PatchStudent, courseId?: number | null) {
    const params = new URLSearchParams()
    if (courseId != null) params.set('course_id', String(courseId))
    const url =
      '/api/account/' + (params.toString() ? `?${params.toString()}` : '')
    return await this.httpClient.patch<Student>(url, update)
  }

  async deleteAccount() {
    await this.httpClient.del<never>(`/api/account/`, {})
  }

  async patchMessage(messageId: number, update: { duration_front: number }) {
    return await this.httpClient.patch<never>(
      `/api/message/${messageId}/`,
      update,
    )
  }

  async getHints() {
    return await this.httpClient.get<AccountHints>('/api/account/hints/')
  }

  async deleteHint(hint: Hints) {
    return await this.httpClient.patch<AccountHints>(
      `/api/account/hint/${hint}/`,
      { flag: false },
    )
  }

  async updatePassword(update: UpdatePassword) {
    await this.httpClient.post<never>('/api/change_password/', update)
  }

  async updateEmail(update: UpdateEmail) {
    await this.httpClient.post<never>('/api/change_email/', update)
  }

  async stat(courseId?: number | null) {
    const params = new URLSearchParams()
    if (courseId != null) params.set('course_id', String(courseId))
    const url =
      '/api/account/stat/' + (params.toString() ? `?${params.toString()}` : '')
    return await this.httpClient.get<Stat>(url)
  }

  async voice_push(chatId: number, file: Blob) {
    //return Promise.resolve({done: true})
    const formData = new FormData()
    formData.append('file', file)
    return this.httpClient.request<{ done: boolean }>(
      '/api/voice_push/' + chatId + '/',
      {
        method: 'POST',
        body: formData,
        // headers: {
        //   'Content-Disposition': 'attachment; filename=file',
        //   'Content-Type': 'audio/wave',
        // },
      },
    )
  }

  vocabulary_add(request: {
    chat_id: number
    word_original: string
    message_id: number
    course_id?: number | null
  }) {
    const { course_id, chat_id, word_original, message_id } = request
    const body: {
      chat_id: number
      word_original: string
      message_id: number
      course_id?: number
    } = { chat_id, word_original, message_id }
    if (course_id != null) body.course_id = course_id
    return this.httpClient.post<Vocabulary>(
      `/api/vocabulary/chat/${chat_id}/add/`,
      body,
    )
  }
  vocabulary_delete(vocabularyId: number) {
    return this.httpClient.del<never>(
      `/api/vocabulary/remove/${vocabularyId}/`,
      {},
    )
  }

  vocabulary(tag: string, courseId?: number | null) {
    const params = new URLSearchParams()
    if (courseId != null) params.set('course_id', String(courseId))
    const url =
      `/api/vocabulary/${tag}/` +
      (params.toString() ? `?${params.toString()}` : '')
    return this.httpClient.get<Vocabulary[]>(url)
  }

  message_rate(id: number, rating: number, user_comment?: string) {
    return this.httpClient.patch<never>(`/api/message_rate/${id}/`, {
      rating,
      user_comment,
    })
  }

  chat_rate(
    id: number,
    rating?: number,
    experience?: number,
    user_comment?: string,
  ) {
    return this.httpClient.patch<never>(`/api/chat_rate/${id}/`, {
      rating,
      experience,
      user_comment,
    })
  }

  subscribe(plan: SubscriptionPlan, provider: SubscriptionProvider) {
    return this.httpClient.post<
      | {
          status: 'success'
          payment_data: CloudPaymentsPaymentData | YaKassaPaymentData
          success_url: string
        }
      | {
          status: 'rejected'
          message: string
        }
    >('/api/subscribe/', { plan, provider })
  }

  subscribe_trial(plan: SubscriptionPlan, provider: SubscriptionProvider) {
    return this.httpClient.post<
      | {
          status: 'success'
          payment_data: CloudPaymentsPaymentData | YaKassaPaymentData
          success_url: string
        }
      | {
          status: 'rejected'
          message: string
        }
    >('/api/subscribe_trial/', { plan, provider })
  }

  // Временно закомментировано - IAP запросы
  // unsubscribe(
  //   subscriptionId: number,
  //   feedback?: { reason: string[]; comment: string; contacts: string },
  // ) {
  //   return this.httpClient.post<{ status: 'success' | 'reject' }>(
  //     `/api/iap/${subscriptionId}/unsubscribe/`,
  //     { feedback },
  //   )
  // }

  // subscriptionsList() {
  //   return this.httpClient.get<Subscription[]>(`/api/iap/`)
  // }

  // async createSubscriptions(
  //   plan: SubscriptionPlan,
  //   provider: SubscriptionProvider,
  // ) {
  //   return await this.httpClient.post<null>(`/api/iap/`, { plan, provider })
  // }

  // async updateSubscriptions(update: Subscription) {
  //   return await this.httpClient.put<Subscription>(
  //     `/api/iap/${update.id}/`,
  //     update,
  //   )
  // }

  // async deleteSubscriptions(update: Subscription) {
  //   return await this.httpClient.del<Subscription>(
  //     `/api/iap/${update.id}/`,
  //     update,
  //   )
  // }

  getVoiceLimit() {
    return this.httpClient.get<{ limit: number; used: number }>(
      '/api/voice/limit/',
    )
  }

  checkVoiceLimit(chatId: number) {
    return this.httpClient.post<{ allowed: boolean }>(
      `/api/voice/check-limit/${chatId}/`,
      {},
    )
  }

  updateVoiceLimit(chatId: number, data: { duration: number }) {
    return this.httpClient.post(`/api/voice/update-limit/${chatId}/`, data)
  }
}
